import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";
import {
  ShoppingBag,
  Search,
  Filter,
  Download,
  RefreshCw,
  DollarSign,
  TrendingUp,
  Package,
  XCircle,
  ArrowLeft,
  Eye,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const LIMIT = 10;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  useEffect(() => {
    fetchData(1);
  }, []);

  const fetchData = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const skip = (pageNumber - 1) * LIMIT;

      const ordersRes = await axios.post(
        `${API}/api/super-admin/orders/search`,
        {
          skip, // ✅ REQUIRED
          limit: LIMIT, // ✅ OPTIONAL (but safe)
        },
        {
          headers: getAdminAuthHeader(),
        }
      );

      setOrders(ordersRes.data.data);
      setTotal(ordersRes.data.total);
      setPage(pageNumber);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };
const downloadInvoice = async (orderId) => {
  try {
    const response = await axios.get(
      `${API}/api/admin/orders/${orderId}/receipt-pdf`,
      {
        headers: getAdminAuthHeader(),
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/pdf",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Receipt-${orderId.substring(0, 8)}.pdf`;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    toast.success("Invoice downloaded");
  } catch (error) {
    console.error(error);
    toast.error("Failed to download invoice");
  }
};
const openOrderDetails = (order) => {
  setSelectedOrder(order);
  setShowOrderModal(true);
};

const closeOrderDetails = () => {
  setSelectedOrder(null);
  setShowOrderModal(false);
};
  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      preparing: "bg-purple-100 text-purple-800",
      ready: "bg-green-100 text-green-800",
      delivered: "bg-teal-100 text-teal-800",
      completed: "bg-gray-100 text-gray-800",
      cancelled: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${
          styles[status] || styles.pending
        }`}
      >
        {status?.toUpperCase()}
      </span>
    );
  };

const formatCurrency = (amount, symbol = "$") => {
  return `${symbol}${Number(amount || 0).toFixed(2)}`;
};

const formatDateTime = (
  dateString,
  locale = "en-US",
  timezone = "UTC"
) => {
  try {
    return new Date(dateString).toLocaleString(locale, {
      timeZone: timezone,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return new Date(dateString).toLocaleString();
  }
};

  return (
    <SuperAdminLayout>
      <div>
        {/* Page Header */}
        <div
          className="mb-8 flex items-center gap-4 w-full  px-6
  bg-gradient-to-r from-orange-500 to-red-500
  shadow-md text-white py-[1.65rem]"
        >
          <Button
            onClick={() => navigate("/admin/dashboard")}
            variant="ghost"
            className="hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold ">Order Monitoring</h1>
            <p className=" mt-1">Track orders across all restaurant outlets</p>
          </div>
        </div>
        <div className="p-8">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Orders
                  </CardTitle>
                  <ShoppingBag className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {stats.total_orders}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Revenue
                  </CardTitle>
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">
                    {formatCurrency(stats.total_revenue)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Gross revenue</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Commission Earned
                  </CardTitle>
                  <TrendingUp className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">
                    {formatCurrency(stats.total_commission)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Platform earnings
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Avg Order Value
                  </CardTitle>
                  <Package className="h-5 w-5 text-gray-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-600">
                    {formatCurrency(stats.average_order_value)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Per order</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters & Actions */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <Input
                      placeholder="Search by order number, table, customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="preparing">Preparing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <Button onClick={() => fetchData(1)} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                {/* <Button className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button> */}
              </div>
            </CardContent>
          </Card>

          {/* Orders Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No orders found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Order ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Restaurant
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Customer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Items
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Commission
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Time
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {orders.map((order) => (
                        <tr key={order.order_id} className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <div className="font-medium text-gray-900">
                              {order.order_number ||
                                order.order_id.substring(0, 8)}
                            </div>
                            {order.table_number && (
                              <div className="text-xs text-gray-500">
                                Table {order.table_number}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {order.restaurant_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.order_type}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {order.customer_name || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {order.items_count} items
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-semibold text-gray-900">
                              {formatCurrency(
                                order.total_amount,
                                order.currency_symbol
                              )}      
                            </div>
                            {order.discount_amount > 0 && (
                              <div className="text-xs text-red-600">
                                -{formatCurrency(order.discount_amount, order.currency_symbol)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-semibold text-blue-600">
                              {formatCurrency(order.commission_amount, order.currency_symbol)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.commission_percentage}%
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {formatDateTime(
                              order.created_at,
                              order.locale,
                              order.timezone
                            )}   
                          </td>
<td className="px-4 py-4">
  <div className="flex items-center gap-2">
    <Button
      size="sm"
      variant="outline"
      onClick={() => openOrderDetails(order)}
    >
      <Eye className="h-4 w-4 mr-2" />
    </Button>

    <Button
      size="sm"
      variant="outline"
      onClick={() => downloadInvoice(order.order_id)}
    >
      <Download className="h-4 w-4 mr-2" />
    </Button>
  </div>
</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {total > LIMIT && (
                    <div className="flex justify-between items-center mt-6">
                      <p className="text-sm text-gray-600">
                        Showing {(page - 1) * LIMIT + 1} –{" "}
                        {Math.min(page * LIMIT, total)} of {total} orders
                      </p>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          disabled={page === 1}
                          onClick={() => fetchData(page - 1)}
                        >
                          Previous
                        </Button>

                        <Button
                          variant="outline"
                          disabled={page * LIMIT >= total}
                          onClick={() => fetchData(page + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Order Details Modal */}
{showOrderModal && selectedOrder && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
      
      {/* Header */}
      <div className="flex items-center justify-between border-b px-6 py-4 sticky top-0 bg-white z-10">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Order Details
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {selectedOrder.order_number ||
              selectedOrder.order_id.substring(0, 8)}
          </p>
        </div>

        <button
          onClick={closeOrderDetails}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="p-6 space-y-6">
        
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">Restaurant</p>
            <p className="font-semibold text-gray-900">
              {selectedOrder.restaurant_name}
            </p>
          </div>

          <div className="border rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">Customer</p>
            <p className="font-semibold text-gray-900">
              {selectedOrder.customer_name || "-"}
            </p>
          </div>

          <div className="border rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">Order Type</p>
            <p className="font-semibold text-gray-900">
              {selectedOrder.order_type || "-"}
            </p>
          </div>
              
          <div className="border rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">Status</p>
            <div className="mt-1">
              {getStatusBadge(selectedOrder.status)}
            </div>
          </div>

          <div className="border rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">Created Time</p>
            <p className="font-semibold text-gray-900">
              {formatDateTime(
                selectedOrder.created_at,
                selectedOrder.locale,
                selectedOrder.timezone
              )}
            </p>
          </div>
        </div>
<div className="border rounded-xl p-4">
  <h3 className="font-semibold mb-3">
    Ordered Items
  </h3>

  <div className="space-y-2">
    {selectedOrder.items?.map((item) => (
      <div
        key={item.item_id}
        className="flex justify-between text-sm"
      >
        <span>
          {item.item_name} × {item.quantity}
        </span>

        <span className="font-medium">
          {formatCurrency(
            item.price * item.quantity,
            selectedOrder.currency_symbol
          )}
        </span>
      </div>
    ))}
  </div>
</div>
        {/* Amount Details */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-900">
            Payment Summary
          </h3>

          <div className="border rounded-xl divide-y">
            <div className="flex justify-between p-4">
              <span>Subtotal</span>
              <span className="font-medium">
                {formatCurrency(
                  selectedOrder.subtotal_amount ||
                    selectedOrder.total_amount,
                  selectedOrder.currency_symbol
                )}
              </span>
            </div>

            <div className="flex justify-between p-4">
              <span>Discount</span>
              <span className="text-red-600 font-medium">
                -{formatCurrency(
                  selectedOrder.discount_amount,
                  selectedOrder.currency_symbol
                )}
              </span>
            </div>

            <div className="flex justify-between p-4">
              <span>Commission</span>
              <span className="text-blue-600 font-medium">
                {formatCurrency(
                  selectedOrder.commission_amount,
                  selectedOrder.currency_symbol
                )}
              </span>
            </div>

            <div className="flex justify-between p-4 bg-gray-50 rounded-b-xl">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-lg">
                {formatCurrency(
                  selectedOrder.total_amount,
                  selectedOrder.currency_symbol
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
    </SuperAdminLayout>
  );
}
